import { Component, OnDestroy, OnInit } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { DialogService } from 'src/app/services/dialog.service';
import { EmailNotificationService } from './emailnotification.service';
import { CountryBackendEmailNotification } from './interfaces/emailnotification';
import { MatTableDataSource } from '@angular/material/table';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CustomDialogService } from 'src/app/services/customdialog.service';
import { DefaultValuesService } from 'src/app/services/defaultvalues.service';

@Component({
  selector: 'emailnotification',
  templateUrl: 'emailnotification.component.html',
  standalone: false
})
export class EmailnotificationComponent implements OnInit, OnDestroy {

  public emailNotifications = new MatTableDataSource<FormGroup>([]);
  private subscriptionStage: any;
  private subscriptionTenant: any;
  isSettingRow = false;
  ENForm!: FormGroup;

  constructor(
    private readonly confirmDialog: DialogService,
    private readonly customDialog: CustomDialogService,
    private readonly toastrService: ToastrService,
    private readonly defaultValuesService: DefaultValuesService,
    private readonly emailNotificationService: EmailNotificationService,
    private readonly formBuilder: FormBuilder) {}

  ngOnInit(): void {
    this.ENForm = this.formBuilder.group({
      ENRows: this.formBuilder.array([])
    });
    this.subscriptionStage = this.defaultValuesService.stageMessageChanges$.subscribe(() => {
      this.getAllEmailNotifications();
    });
    this.subscriptionTenant = this.defaultValuesService.tenantMessageChanges$.subscribe(() => {
      this.getAllEmailNotifications();
    });
    this.getAllEmailNotifications();
  }

  ngOnDestroy(): void {
    this.subscriptionStage.unsubscribe();
    this.subscriptionTenant.unsubscribe();
  }

  // Form and row config

  get columnsToDisplay(): string[] {
    const baseColumns = ['name', 'email', 'isactive', 'edit'];
    if (!this.isSettingRow) {
      baseColumns.push('delete');
    }
    baseColumns.push('headeraddbutton');
    return baseColumns;
  }

  get ENRows(): FormArray<FormGroup> {
    return this.ENForm.get('ENRows') as FormArray<FormGroup>;
  }

  private buildRow(data?: Partial<CountryBackendEmailNotification>, forceEditMode = false): FormGroup {
    const hasValidId = data?.emailNotificationId != null;
    const isEditable = forceEditMode ? false : hasValidId;

    return this.formBuilder.group({
      emailNotificationId: [data?.emailNotificationId ?? null],
      name: [data?.name ?? '', Validators.required],
      email: [data?.email ?? '', [Validators.required, Validators.email]],
      isActive: [data?.isActive ?? true],
      isDeleted: [data?.isDeleted ?? false],
      isEditable: [isEditable],
      originalData: [null]
    });
  }

  addNewRow() {
    const newRow: Partial<CountryBackendEmailNotification> = {
      name: '',
      email: '',
      isActive: true,
      isDeleted: false,
      emailNotificationId: undefined
    }; 

    this.ENRows.insert(0, this.buildRow(newRow, true));
    this.emailNotifications.data = this.ENRows.controls as FormGroup[];
    this.isSettingRow = true;
  }

  editRow(index: number) {
    const row = this.ENRows.at(index);
    if (!row) return;

    row.patchValue({
      originalData: row.getRawValue(),
      isEditable: false
    });

    this.isSettingRow = true;
  }

  cancelEdit(index: number) {
    const row = this.ENRows.at(index);
    if (!row) return;

    const originalData = row.get('originalData')?.value;

    if (originalData) {
      row.patchValue({ ...originalData, isEditable: true, originalData: null });
    } else {
      this.ENRows.removeAt(index);
      this.emailNotifications.data = this.ENRows.controls as FormGroup[];
    }

    this.isSettingRow = false;
  }

  // HTTP calls and actions

  getAllEmailNotifications() {
    this.emailNotificationService.getAllEmailNotifications().subscribe(res => {
      this.ENRows.clear();
      const data = res ?? [];
      data.forEach(item => this.ENRows.push(this.buildRow(item)));
      this.emailNotifications.data = this.ENRows.controls as FormGroup[];
      this.isSettingRow = false;
    });
  }

  createOrUpdateEmail(index: number) {
    const row = this.ENRows.at(index);
    if (!row) return;

    row.markAllAsTouched();
    if (row.invalid) {
      this.toastrService.error('Fill all requiered fields correctly before saving.', 'Validation Error');
      return;
    }

    const vm: CountryBackendEmailNotification = {
      emailNotificationId: row.get('emailNotificationId')?.value || 0,
      name: row.get('name')?.value,
      email: row.get('email')?.value,
      isActive: row.get('isActive')?.value,
      isDeleted: row.get('isDeleted')?.value,
      tenantId: row.get('tenantId')?.value,
      stageId: row.get('stageId')?.value
    };
    const id = vm.emailNotificationId;

    if (id) {
      this.customDialog.openConfirm({
        title: "Update Email Notification",
        message: `Do you want to update the email notification? To proceed, click 'Yes'.`,
        caller: this
      });
      this.customDialog.confirmed().subscribe(res => {
        if (!res.confirmed) return;
        this.emailNotificationService.updateEmailNotification(vm, id).subscribe(res => {
          if (res) {
            row.patchValue({ isEditable: true, originalData: null });
            this.confirmDialog.showSuccessDialog('Entry updated successfully', 'Entry has been updated');
            this.isSettingRow = false;
          }
        });
      });
    } else {
      this.customDialog.openConfirm({
        title: "Create Email Notification",
        message: `Do you want to create a new email notification? To proceed, click 'Yes'.`,
        caller: this
      });
      this.customDialog.confirmed().subscribe(res => {
        if (!res.confirmed) return;
        this.emailNotificationService.createEmailNotification(vm).subscribe((res) => {
          if (res) {
            const newId = res.emailNotificationId ?? res;
            row.patchValue({
              emailNotificationId: newId,
              isEditable: true,
              originalData: null
            });

            this.confirmDialog.showSuccessDialog('Entry created successfully', 'Entry has been created');
            this.isSettingRow = false;
          }
        });
      });
    }
  }

  deleteEmailNotification(item: CountryBackendEmailNotification) {
    if (!item) return;
    const id = item.emailNotificationId;
    const email = item.email;
    const index = this.ENRows.controls.findIndex(r => r.get('emailNotificationId')?.value === id);

    this.customDialog.openConfirm({
      title: "Delete Email Notification",
      message: `The entry with the email '${email}' will be deleted. To proceed the deletion, click 'Yes'.`,
      caller: this
    });
    this.customDialog.confirmed().subscribe(res => {
      if (!res.confirmed) return;
      if (id) {
        this.emailNotificationService.deleteEmailNotification(id).subscribe(res => {
          if (res) {
            this.ENRows.removeAt(index);
            this.emailNotifications.data = this.ENRows.controls as FormGroup[];
            this.confirmDialog.showSuccessDialog('Entry deleted successfully', 'Entry has been deleted');
          }
        });
      } else {
        this.ENRows.removeAt(index);
        this.emailNotifications.data = this.ENRows.controls as FormGroup[];
      }
    });
  }
}